/**
 * barraDeStatus — pinta a BARRA DO SISTEMA (relógio, sinal, bateria) com o piso
 * do app, em vez do preto que o Median desenha por padrão.
 *
 * Por que isto existe, se o `<meta name="theme-color">` já é atualizado
 * ---------------------------------------------------------------------
 * `theme-color` é contrato de NAVEGADOR. Dentro do wrapper Median a barra do
 * topo é uma view NATIVA, fora do webview: ela ignora o meta, ignora o CSS e
 * ignora o `background-color` do `<html>`. Quem manda nela é a ponte JS do
 * Median (`median.statusbar.set`). Por isso o app aparecia com a faixa preta
 * sobre o piso tinta (#1A1713) — dois pretos diferentes encostados, que é
 * justamente o que o design system não admite (✅ Pedro, 24/08).
 *
 * Fora do Median (web, PWA, Capacitor) esta função é NO-OP. A ponte não existe,
 * e quem pinta o topo lá é o `theme-color`, que o `useTheme` já mantém.
 *
 * ⚠️ O `overlay` NÃO É ESCOLHIDO AQUI — ele é LIDO do aparelho.
 * -----------------------------------------------------------
 * `overlay` decide se o webview começa ABAIXO da barra (false) ou por BAIXO
 * dela (true), e isso muda a geometria da tela inteira: com `true` o app ganha
 * 48px de altura e paga de volta em `env(safe-area-inset-top)`. Qual dos dois
 * está ligado é configuração do painel do Median, que não vive neste repo — e
 * chutar aqui deslocaria o app inteiro no aparelho de quem tem o outro.
 *
 * Então a gente MEDE: `env(safe-area-inset-top)` só é maior que zero quando o
 * webview está por baixo da barra. Lendo o inset e devolvendo o mesmo valor,
 * esta função nunca muda o enquadramento — muda só a cor, que é o que foi
 * pedido. Vale em qualquer aparelho e nos dois sistemas.
 *
 * ⚠️ COR SEM `#`. A ponte aceita `RRGGBB` ou `AARRGGBB` — mandar `#1A1713` é
 * ignorado em silêncio.
 *
 * ⚠️ `style` DESCREVE O FUNDO, NÃO OS ÍCONES — e isto é MEDIDO no aparelho,
 * não deduzido. A primeira versão daqui deduziu o contrário (convenção do iOS,
 * `lightContent` = ícones claros) e saiu invertida nos DOIS temas: no piso
 * papel os ícones vinham brancos e sumiam, no piso tinta vinham pretos e
 * sumiam (✅ Pedro, 24/08, no iPhone).
 *
 * Com `style` sendo o fundo, ele é literalmente o nosso modo de tema — daí não
 * haver mais conversão nenhuma abaixo, e não haver mais como inverter: some o
 * ternário, some o erro. É a convenção do Capacitor, não a do iOS.
 * Ref: https://docs.median.co/docs/status-bar
 */

/** Os dois pisos do design system. #000 e #FFF são banidos — ver `index.css`. */
export const PISO_DO_APP = { light: "#F9F5F0", dark: "#1A1713" } as const;

export type ModoDeTema = keyof typeof PISO_DO_APP;

/**
 * Quantos pixels o webview avança POR BAIXO da barra do sistema.
 *
 * Não dá para ler `--sv-safe-top` com `getPropertyValue`: propriedade
 * customizada devolve o texto como foi escrito (`env(safe-area-inset-top, 0px)`),
 * sem resolver o `env()`. A sonda abaixo resolve porque o valor cai numa
 * propriedade REAL, que o browser computa.
 */
const lerRecuoDoTopo = (): number => {
  if (typeof document === "undefined") return 0;
  const sonda = document.createElement("div");
  sonda.style.cssText =
    "position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px)";
  document.body.appendChild(sonda);
  const recuo = Number.parseFloat(getComputedStyle(sonda).paddingTop) || 0;
  sonda.remove();
  return recuo;
};

type PonteDaBarra = {
  set?: (opcoes: { style: string; color: string; overlay: boolean }) => void;
};

const ponte = (): PonteDaBarra | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { median?: { statusbar?: PonteDaBarra } }).median?.statusbar;
};

/** Guarda o último modo pedido, para o `median_library_ready` repetir a ordem. */
let ultimoModo: ModoDeTema | null = null;

const aplicar = (modo: ModoDeTema): void => {
  const barra = ponte();
  if (!barra?.set) return;
  try {
    barra.set({
      // Sem ternário de propósito — ver o ⚠️ do cabeçalho. `style` é o fundo.
      style: modo,
      color: PISO_DO_APP[modo].replace("#", ""),
      overlay: lerRecuoDoTopo() > 0,
    });
  } catch {
    // Cor de barra de sistema não pode derrubar a troca de tema.
  }
};

/**
 * Pede a repintura. Chamável a cada troca de tema; é idempotente e barata.
 *
 * ⚠️ A PRIMEIRA CHAMADA COSTUMA CHEGAR CEDO DEMAIS. O React monta antes de o
 * Median injetar a ponte, então `window.median` ainda é `undefined` e o `set`
 * acima cai fora sem fazer nada. Por isso o pedido fica guardado e o
 * `median_library_ready` — o gancho que o próprio Median chama quando a ponte
 * existe — repete a ordem. Sem essa segunda tentativa o app abriria com a
 * barra preta e só corrigiria quando a pessoa trocasse de tema no Perfil.
 */
export const pintarBarraDeStatus = (modo: ModoDeTema): void => {
  ultimoModo = modo;
  aplicar(modo);
};

let ganchoInstalado = false;

/**
 * Encadeia o `median_library_ready`. Encadeia, e não sobrescreve: é um global
 * único, e roubá-lo silenciaria qualquer outro trecho que dependa dele.
 */
export const observarPonteDoMedian = (): void => {
  if (ganchoInstalado || typeof window === "undefined") return;
  ganchoInstalado = true;
  /* ⚠️ O `try` NÃO É PARANOIA — é a lição de 24/08, e ela custa duas linhas.
     Esta função é chamada do efeito do `ThemeProvider`, que embrulha o app
     INTEIRO: o que estoura aqui apaga a tela antes de qualquer coisa existir.
     E a linha de baixo é uma ATRIBUIÇÃO A GLOBAL dentro de módulo ES, que é
     strict mode — se o Median um dia expuser `median_library_ready` como
     não-gravável ou só-com-getter, ela lança `TypeError`. Medido contra o
     bundle de produção: nos dois casos o app não abre.
     Hoje o global é gravável no Median (o app rodou o dia inteiro com este
     código no ar). Mas "hoje funciona" não é garantia num wrapper que
     atualiza sozinho — e o preço de estar errado aqui é o app inteiro, não
     uma barra colorida. Perder a repintura tardia é cosmético; perder o boot
     não é. */
  try {
    const janela = window as Window & { median_library_ready?: () => void };
    const anterior = janela.median_library_ready;
    janela.median_library_ready = () => {
      anterior?.();
      if (ultimoModo) aplicar(ultimoModo);
    };
  } catch {
    // Sem o gancho, a barra só repinta na próxima troca de tema. O app abre.
  }
};
